import { useCallback, useEffect, useRef, useState } from "react";

import { useAppDispatch } from "../redux/hooks";
import { emptyApi } from "./emptyApi";

type VisualizationsArgs = {
  orgPlayerId: string;
  sessionId: string;
  movementTypeId: number;
};

// Default metrics for different movement types
const getDefaultDesiredMetrics = (movementTypeId: number): string[] => {
  if (movementTypeId === 1) {
    // baseball-hitting
    return [
      "bat_speed",
      "attack_angle",
      "exit_speed",
      "launch_angle",
      "field_direction",
    ];
  } else {
    // baseball-pitching (movementTypeId === 2)
    return [
      "lead_arm_vert_ang",
      "rear_hip_ir_range_full_max",
      "lead_leg_vert_ang",
    ];
  }
};

export const visualizationsExtendedApi = emptyApi.injectEndpoints({
  endpoints: (builder) => ({
    getVisualizations: builder.query<
      any,
      VisualizationsArgs & { page?: number } & { desiredMetrics?: string[] }
    >({
      query: ({
        orgPlayerId,
        sessionId,
        movementTypeId,
        desiredMetrics,
        page = 1,
      }) => {
        // Use provided desiredMetrics or default based on movementTypeId
        const metrics =
          desiredMetrics || getDefaultDesiredMetrics(movementTypeId);

        const params = new URLSearchParams({
          org_player_id: orgPlayerId,
          session_id: sessionId,
          movement_type_id: movementTypeId.toString(),
          time_series_type: "inverse-kinematics",
        });

        // Add desired metrics as multiple parameters
        metrics.forEach((metric) => {
          params.append("desired_metrics", metric);
        });

        // Add page parameter
        params.append("page", page.toString());

        return {
          url: `/visualizations?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: (result, error, args) => [
        { type: "Visualizations", id: `${args.orgPlayerId}-${args.sessionId}` },
      ],
    }),
  }),
});

export const { useGetVisualizationsQuery } = visualizationsExtendedApi;

// Normalize RTK Query / FetchBaseQuery errors to have a displayable message
const normalizeError = (err: any): { status?: number; message: string } => {
  if (!err || typeof err !== "object") {
    return { message: "Failed to load movement data. Please try again." };
  }
  const message =
    err.message ??
    err.data?.detail ??
    (typeof err.data === "string" ? err.data : null) ??
    err.data?.message ??
    "Failed to load movement data. Please try again.";
  return { ...err, message };
};

// Custom hook to fetch all pages of visualizations data in parallel.
// Fetches page 1 first to get total_pages, then fetches remaining pages concurrently.
export const useGetAllVisualizationsQuery = (args: VisualizationsArgs) => {
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<any>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const dispatch = useAppDispatch();
  const hasFetchedForRef = useRef<string>("");

  const argsKey = `${args.orgPlayerId}-${args.sessionId}-${args.movementTypeId}`;

  useEffect(() => {
    const fetchKey = argsKey;
    if (hasFetchedForRef.current === fetchKey) return;

    hasFetchedForRef.current = fetchKey;
    setIsLoading(true);
    setIsError(false);
    setError(null);

    const subscriptions: { unsubscribe: () => void }[] = [];

    const fetchAllParallel = async () => {
      try {
        const page1Thunk = dispatch(
          visualizationsExtendedApi.endpoints.getVisualizations.initiate({
            ...args,
            page: 1,
          }),
        );
        subscriptions.push(page1Thunk);
        const page1Result = await page1Thunk.unwrap();

        if (hasFetchedForRef.current !== fetchKey) return;

        const page1Data = page1Result?.data || [];
        const pagination = page1Result?.pagination;
        const total = pagination?.total_pages ?? 1;

        setTotalPages(total);

        if (total <= 1) {
          setAllData(page1Data);
          return;
        }

        const remainingPages = Array.from(
          { length: total - 1 },
          (_, i) => i + 2,
        );
        const remainingThunks = remainingPages.map((page) =>
          dispatch(
            visualizationsExtendedApi.endpoints.getVisualizations.initiate({
              ...args,
              page,
            }),
          ),
        );
        subscriptions.push(...remainingThunks);
        const rawResults = await Promise.all(
          remainingThunks.map((t) => t.unwrap()),
        );

        if (hasFetchedForRef.current !== fetchKey) return;

        const remainingData = rawResults.flatMap(
          (r: { data?: any[] }) => r?.data ?? [],
        );
        setAllData([...page1Data, ...remainingData]);
      } catch (err) {
        if (hasFetchedForRef.current === fetchKey) {
          hasFetchedForRef.current = "";
          setIsError(true);
          setError(normalizeError(err));
          setIsLoading(false); // Must set here: we cleared hasFetchedForRef so finally won't run it
        }
      } finally {
        if (hasFetchedForRef.current === fetchKey) {
          setIsLoading(false);
        }
      }
    };

    fetchAllParallel();

    return () => {
      subscriptions.forEach((s) => s.unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [argsKey, dispatch, refetchTrigger]);

  const refetch = useCallback(() => {
    hasFetchedForRef.current = "";
    setRefetchTrigger((t) => t + 1);
  }, []);

  return {
    data: allData,
    isLoading,
    isError,
    error,
    refetch,
    pagination: {
      currentPage: 1,
      totalPages,
      hasNextPage: false,
    },
  };
};
